import { useState, useEffect, useCallback, useRef } from "react";
import { salesAPI, expenseAPI, haulAPI, verdictAPI } from "@/lib/api";
import {
  ArrowUp, ArrowDown, CurrencyDollar, Receipt, Truck, Package,
  Car, Spinner, ArrowsClockwise, Export, CaretDown,
  TrendUp, ChartPie, Trophy, Warning, Plus, X,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import BottomNav from "@/components/app/BottomNav";
import FinanceTabs from "@/components/app/FinanceTabs";

const PERIODS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "all", label: "All Time" },
];

export default function PnLDashboardPage() {
  const [period, setPeriod] = useState("all");
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [syncStatus, setSyncStatus] = useState(null);
  const [view, setView] = useState("overview");
  const [showAddSale, setShowAddSale] = useState(false); // overview | items | hauls

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, salesRes, expRes, statusRes] = await Promise.all([
        salesAPI.summary(period),
        salesAPI.list(200),
        expenseAPI.list(200),
        salesAPI.syncStatus(),
      ]);
      setSummary(sumRes.data);
      setSales(salesRes.data.sales || []);
      setExpenses(expRes.data.expenses || []);
      setSyncStatus(statusRes.data);
    } catch {} finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const { data } = await salesAPI.sync();
      if (data.throttled) {
        setSyncMsg(data.message);
      } else {
        setSyncMsg(`Synced: ${data.new || 0} new, ${data.updated || 0} updated`);
        await loadData();
      }
    } catch (err) {
      setSyncMsg(err.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
    setTimeout(() => setSyncMsg(""), 5000);
  };

  const handleExport = () => {
    if (!sales.length) return;
    const headers = "Date,Item,Brand,Sale Price,eBay Fees,Shipping,COGS,Net Profit\n";
    const rows = sales.map((s) =>
      `${s.sale_date?.split("T")[0] || ""},${(s.category || "").replace(/,/g, ";")},${s.brand || ""},${s.total_amount || 0},${s.total_fees || 0},${s.shipping_label_cost || 0},${s.cogs || 0},${s.net_profit || 0}`
    ).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `graborskip-pnl-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Spinner size={32} weight="bold" className="animate-spin" />
      </div>
    );
  }

  const s = summary || {};
  const hasData = s.total_sales > 0 || expenses.length > 0;

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white">
        <FinanceTabs />
        <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200">
          <h1 className="font-heading text-xl font-black tracking-tighter uppercase">P&L</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddSale(true)}
              className="p-2 bg-black text-white border-2 border-black active:translate-y-[1px]"
              title="Add manual sale"
              data-testid="add-sale-btn"
            >
              <Plus size={16} weight="bold" />
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="p-2 border-2 border-black hover:bg-neutral-100 disabled:opacity-50"
              title="Sync eBay sales"
              data-testid="sync-sales-btn"
            >
              <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleExport}
              disabled={!sales.length}
              className="p-2 border-2 border-black hover:bg-neutral-100 disabled:opacity-30"
              title="Export CSV"
              data-testid="export-csv-btn"
            >
              <Export size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Sync status */}
      {syncMsg && (
        <div className="px-6 py-2 border-b-2 border-black bg-amber-50 font-body text-xs font-semibold text-amber-800" data-testid="sync-message">
          {syncMsg}
        </div>
      )}
      {syncStatus && (
        <div className="px-6 py-1.5 border-b border-neutral-200 font-body text-[10px] text-neutral-400 flex items-center justify-between">
          <span>{s.total_sales || 0} sale{s.total_sales !== 1 ? "s" : ""} tracked</span>
          <span>{syncStatus.last_synced ? `Synced ${timeAgo(syncStatus.last_synced)}` : "Never synced"}</span>
        </div>
      )}

      {/* Period tabs */}
      <div className="grid grid-cols-4 border-b-2 border-black">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`p-2.5 font-heading text-xs font-bold uppercase tracking-wide text-center transition-colors ${
              period === p.key ? "bg-black text-white" : "hover:bg-neutral-100"
            }`}
            data-testid={`period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {!hasData ? (
        <EmptyState onSync={handleSync} syncing={syncing} />
      ) : (
        <>
          {/* View tabs */}
          <div className="grid grid-cols-3 border-b border-neutral-200">
            {[
              { key: "overview", icon: <ChartPie size={12} weight="bold" />, label: "Overview" },
              { key: "items", icon: <Package size={12} weight="bold" />, label: "Items" },
              { key: "hauls", icon: <Truck size={12} weight="bold" />, label: "Hauls" },
            ].map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`p-2 font-body text-xs font-semibold uppercase flex items-center justify-center gap-1 ${
                  view === v.key ? "border-b-2 border-black text-black" : "text-neutral-400"
                }`}
                data-testid={`view-${v.key}`}
              >
                {v.icon} {v.label}
              </button>
            ))}
          </div>

          {view === "overview" && <OverviewTab summary={s} sales={sales} expenses={expenses} />}
          {view === "items" && <ItemsTab sales={sales} />}
          {view === "hauls" && <HaulsTab summary={s} />}
        </>
      )}

      {showAddSale && (
        <AddSaleModal
          onClose={() => setShowAddSale(false)}
          onSaved={async () => { setShowAddSale(false); await loadData(); }}
        />
      )}

      <BottomNav />
    </div>
  );
}


function EmptyState({ onSync, syncing }) {
  return (
    <div className="p-8 text-center" data-testid="pnl-empty">
      <TrendUp size={48} weight="bold" className="mx-auto text-neutral-300 mb-4" />
      <div className="font-heading text-xl font-bold uppercase mb-2">No Sales Data Yet</div>
      <p className="font-body text-sm text-neutral-500 mb-4">
        Connect your eBay account and sync to see your P&L.
      </p>
      <button
        onClick={onSync}
        disabled={syncing}
        className="px-6 py-3 bg-black text-white font-heading font-bold uppercase text-sm border-2 border-black disabled:opacity-50 flex items-center gap-2 mx-auto"
      >
        <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
        {syncing ? "Syncing..." : "Sync eBay Sales"}
      </button>
    </div>
  );
}


function OverviewTab({ summary, sales, expenses }) {
  const s = summary;
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const profitPositive = s.net_profit >= 0;

  // Fee breakdown for pie chart
  const feeData = Object.entries(s.fee_breakdown || {}).map(([name, value]) => ({
    name: name.replace(/_/g, " ").replace(/FINAL VALUE FEE/i, "FVF"),
    value: Math.round(value * 100) / 100,
  })).filter((d) => d.value > 0);

  // Monthly profit trend from sales
  const monthlyData = {};
  sales.forEach((sale) => {
    const date = sale.sale_date?.split("T")[0] || "";
    const month = date.substring(0, 7); // YYYY-MM
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { revenue: 0, profit: 0 };
      monthlyData[month].revenue += sale.total_amount || 0;
      monthlyData[month].profit += sale.net_profit || 0;
    }
  });
  const trendData = Object.entries(monthlyData)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, data]) => ({
      month: month.substring(5), // MM only
      revenue: Math.round(data.revenue),
      profit: Math.round(data.profit),
    }));

  const PIE_COLORS = ["#000000", "#002FA7", "#FF1744", "#00E676", "#FF9100", "#7C4DFF", "#00BCD4"];

  return (
    <div className="p-4 space-y-6">
      {/* Net Profit Hero */}
      <div className={`border-4 border-black p-5 text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] ${profitPositive ? "bg-[#00E676]" : "bg-[#FF1744]"}`}>
        <div className="font-body text-xs font-bold uppercase tracking-wider opacity-70">Net Profit</div>
        <div className="font-heading text-4xl font-black tracking-tight flex items-center justify-center gap-2">
          {profitPositive ? <ArrowUp size={28} weight="bold" /> : <ArrowDown size={28} weight="bold" />}
          ${Math.abs(s.net_profit || 0).toFixed(2)}
        </div>
        <div className="font-body text-xs mt-1 opacity-70">{s.total_sales} sale{s.total_sales !== 1 ? "s" : ""} this period</div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-2">
        <SummaryCard icon={<CurrencyDollar size={14} weight="bold" />} label="Revenue" value={`$${(s.total_revenue || 0).toFixed(2)}`} />
        <SummaryCard icon={<Receipt size={14} weight="bold" />} label="eBay Fees" value={`-$${(s.total_fees || 0).toFixed(2)}`} negative />
        <SummaryCard icon={<Truck size={14} weight="bold" />} label="Shipping" value={`-$${(s.total_shipping || 0).toFixed(2)}`} negative />
        <SummaryCard icon={<Package size={14} weight="bold" />} label="COGS" value={`-$${(s.total_cogs || 0).toFixed(2)}`} negative />
        <SummaryCard icon={<Receipt size={14} weight="bold" />} label="Expenses" value={`-$${totalExpenses.toFixed(2)}`} negative />
        <SummaryCard icon={<Car size={14} weight="bold" />} label={`Mileage (${s.total_mileage_miles || 0} mi)`} value={`-$${(s.mileage_deduction || 0).toFixed(2)}`} negative />
      </div>

      {/* Avg days to sell */}
      {s.avg_days_to_sell > 0 && (
        <div className="border-2 border-black p-3 text-center">
          <span className="font-body text-xs text-neutral-500 uppercase">Avg Days to Sell</span>
          <div className="font-heading text-2xl font-black">{s.avg_days_to_sell}</div>
        </div>
      )}

      {/* Profit Trend Chart */}
      {trendData.length > 1 && (
        <div>
          <SectionLabel icon={<TrendUp size={12} weight="bold" />} label="Profit Trend" />
          <div className="border-2 border-black p-3 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                <YAxis tick={{ fontSize: 10 }} width={40} />
                <Tooltip
                  contentStyle={{ border: "2px solid black", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }}
                  formatter={(value) => [`$${value}`, ""]}
                />
                <Bar dataKey="profit" fill="#00E676" stroke="#000" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Fee Breakdown Pie */}
      {feeData.length > 0 && (
        <div>
          <SectionLabel icon={<ChartPie size={12} weight="bold" />} label="Fee Breakdown" />
          <div className="border-2 border-black p-3 flex items-center gap-4">
            <div className="w-32 h-32 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={feeData} dataKey="value" cx="50%" cy="50%" outerRadius={55} stroke="#000" strokeWidth={1}>
                    {feeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1">
              {feeData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="font-body text-xs flex-1 truncate">{d.name}</span>
                  <span className="font-heading text-xs font-bold">${d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Items */}
      {(s.top_items || []).length > 0 && (
        <div>
          <SectionLabel icon={<Trophy size={12} weight="bold" />} label="Top Items by Profit" />
          <div className="border-2 border-black divide-y divide-neutral-200">
            {s.top_items.slice(0, 5).map((item, i) => (
              <div key={i} className="p-2.5 flex items-center gap-3">
                <span className="font-heading text-sm font-black w-5 text-center text-neutral-400">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-xs font-semibold truncate">{item.category || item.brand || "Item"}</div>
                  <div className="font-body text-[10px] text-neutral-500">{item.brand}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-sm font-bold">${(item.sale_price || 0).toFixed(2)}</div>
                  <div className={`font-body text-[10px] font-semibold ${item.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {item.net_profit >= 0 ? "+" : ""}${(item.net_profit || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ItemsTab({ sales }) {
  if (!sales.length) {
    return (
      <div className="p-8 text-center">
        <Package size={40} weight="bold" className="mx-auto text-neutral-300 mb-3" />
        <div className="font-heading text-lg font-bold uppercase">No Sales Yet</div>
        <p className="font-body text-xs text-neutral-500 mt-1">Sync your eBay account to see per-item P&L</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-5 px-3 py-2 border-b border-neutral-200 font-body text-[10px] font-bold uppercase text-neutral-400">
        <span className="col-span-2">Item</span>
        <span className="text-right">Sale</span>
        <span className="text-right">Fees</span>
        <span className="text-right">Profit</span>
      </div>
      {sales.map((sale) => (
        <div key={sale.id || sale.ebay_transaction_id} className="grid grid-cols-5 px-3 py-2.5 border-b border-neutral-100 items-center" data-testid={`sale-item-${sale.id}`}>
          <div className="col-span-2 min-w-0">
            <div className="font-body text-xs font-semibold truncate">{sale.category || sale.brand || "Item"}</div>
            <div className="font-body text-[10px] text-neutral-400">
              {sale.sale_date?.split("T")[0] || ""}
              {sale.days_to_sell > 0 && <span className="ml-1">({sale.days_to_sell}d)</span>}
            </div>
          </div>
          <div className="text-right font-heading text-xs font-bold">${(sale.total_amount || 0).toFixed(2)}</div>
          <div className="text-right font-body text-xs text-neutral-500">-${(sale.total_fees || 0).toFixed(2)}</div>
          <div className={`text-right font-heading text-xs font-bold ${(sale.net_profit || 0) >= 0 ? "text-green-700" : "text-red-600"}`}>
            {(sale.net_profit || 0) >= 0 ? "+" : ""}${(sale.net_profit || 0).toFixed(2)}
          </div>
        </div>
      ))}
    </div>
  );
}


function HaulsTab({ summary }) {
  const [haulPnls, setHaulPnls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    loadHaulPnls();
  }, []);

  const loadHaulPnls = async () => {
    try {
      // Get sales grouped by haul
      const { data } = await salesAPI.list(500);
      const haulMap = {};
      for (const sale of data.sales || []) {
        const hid = sale.haul_id || "__none__";
        if (!haulMap[hid]) {
          haulMap[hid] = { haul_id: hid, sales: [], revenue: 0, fees: 0, shipping: 0, profit: 0 };
        }
        haulMap[hid].sales.push(sale);
        haulMap[hid].revenue += sale.total_amount || 0;
        haulMap[hid].fees += sale.total_fees || 0;
        haulMap[hid].shipping += sale.shipping_label_cost || 0;
        haulMap[hid].profit += sale.net_profit || 0;
      }
      setHaulPnls(Object.values(haulMap).sort((a, b) => b.profit - a.profit));
    } catch {} finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center"><Spinner size={24} className="animate-spin mx-auto" /></div>;
  }

  if (!haulPnls.length) {
    return (
      <div className="p-8 text-center">
        <Truck size={40} weight="bold" className="mx-auto text-neutral-300 mb-3" />
        <div className="font-heading text-lg font-bold uppercase">No Haul Data</div>
        <p className="font-body text-xs text-neutral-500 mt-1">Sales will be grouped by haul after syncing</p>
      </div>
    );
  }

  return (
    <div>
      {haulPnls.map((haul) => (
        <div key={haul.haul_id} className="border-b-2 border-black">
          <button
            onClick={() => setExpanded(expanded === haul.haul_id ? null : haul.haul_id)}
            className="w-full p-3 flex items-center gap-3 text-left hover:bg-neutral-50"
            data-testid={`haul-pnl-${haul.haul_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-bold truncate">
                {haul.haul_id === "__none__" ? "Unlinked Sales" : `Haul ${haul.haul_id.substring(0, 8)}...`}
              </div>
              <div className="font-body text-xs text-neutral-500">{haul.sales.length} item{haul.sales.length !== 1 ? "s" : ""} sold</div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading text-sm font-bold">${haul.revenue.toFixed(2)}</div>
              <div className={`font-body text-xs font-semibold ${haul.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                {haul.profit >= 0 ? "+" : ""}${haul.profit.toFixed(2)}
              </div>
            </div>
            <CaretDown size={14} weight="bold" className={`transition-transform ${expanded === haul.haul_id ? "rotate-180" : ""}`} />
          </button>
          {expanded === haul.haul_id && (
            <div className="border-t border-neutral-200 bg-neutral-50 p-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="font-body text-[10px] uppercase text-neutral-500">Revenue</div>
                  <div className="font-heading text-sm font-bold">${haul.revenue.toFixed(2)}</div>
                </div>
                <div>
                  <div className="font-body text-[10px] uppercase text-neutral-500">Fees</div>
                  <div className="font-heading text-sm font-bold text-red-600">-${haul.fees.toFixed(2)}</div>
                </div>
                <div>
                  <div className="font-body text-[10px] uppercase text-neutral-500">Shipping</div>
                  <div className="font-heading text-sm font-bold text-red-600">-${haul.shipping.toFixed(2)}</div>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {haul.sales.map((sale, i) => (
                  <div key={i} className="flex items-center justify-between font-body text-xs py-1 border-t border-neutral-200 first:border-0">
                    <span className="truncate flex-1">{sale.category || sale.brand || "Item"}</span>
                    <span className="font-semibold ml-2">${(sale.total_amount || 0).toFixed(2)}</span>
                    <span className={`ml-2 font-semibold ${(sale.net_profit || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {(sale.net_profit || 0) >= 0 ? "+" : ""}${(sale.net_profit || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


function SummaryCard({ icon, label, value, negative }) {
  return (
    <div className="border-2 border-black p-2.5">
      <div className="flex items-center gap-1 font-body text-[10px] font-semibold uppercase text-neutral-500">
        {icon} {label}
      </div>
      <div className={`font-heading text-lg font-black mt-0.5 ${negative ? "text-neutral-700" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 font-heading text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">
      {icon} {label}
    </div>
  );
}


function AddSaleModal({ onClose, onSaved }) {
  const [mode, setMode] = useState("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState({
    item_name: "",
    brand: "",
    sale_price: "",
    shipping_cost: "",
    fees: "",
    cogs: "",
    sale_date: new Date().toISOString().split("T")[0],
    platform: "eBay",
    haul_id: "",
    notes: "",
    verdict_id: "",
  });
  const [hauls, setHauls] = useState([]);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    haulAPI.list().then(({ data }) => setHauls((data || []).filter((h) => !h.is_virtual))).catch(() => {});
  }, []);

  const handleSearch = (q) => {
    setQuery(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await verdictAPI.search(q);
        setResults(data || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
  };

  const selectItem = (item) => {
    setSelectedItem(item);
    const high = item.list_price_high || item.list_price_low || 0;
    setForm({
      ...form,
      item_name: item.title_keywords || "",
      brand: item.brand || "",
      haul_id: item.haul_id || "",
      verdict_id: item.id || "",
      sale_price: high ? String(high) : "",
    });
    setMode("form");
  };

  const goManual = () => {
    setSelectedItem(null);
    setForm({ ...form, item_name: query, verdict_id: "" });
    setMode("form");
  };

  const handleSave = async () => {
    if (!form.item_name || !form.sale_price) return;
    setSaving(true);
    try {
      await salesAPI.addManual({
        ...form,
        sale_price: parseFloat(form.sale_price) || 0,
        shipping_cost: parseFloat(form.shipping_cost) || 0,
        fees: parseFloat(form.fees) || 0,
        cogs: parseFloat(form.cogs) || 0,
      });
      onSaved();
    } catch {}
    setSaving(false);
  };

  const price = parseFloat(form.sale_price) || 0;
  const fees = parseFloat(form.fees) || 0;
  const ship = parseFloat(form.shipping_cost) || 0;
  const cogs = parseFloat(form.cogs) || 0;
  const profit = price - fees - ship - cogs;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const moneyField = (key, label, testid) => (
    <div>
      <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">{label}</label>
      <div className="flex items-center">
        <span className="font-body text-sm font-bold mr-1">$</span>
        <input type="text" inputMode="decimal" value={form[key]}
          onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) set(key, v); }}
          placeholder="0.00" className="w-full p-1.5 border-2 border-black font-body text-sm" data-testid={testid} />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center pb-[72px] sm:pb-0">
      <div className="bg-white border-t-4 sm:border-4 border-black w-full sm:max-w-md sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b-2 border-black shrink-0">
          <div className="flex items-center gap-2">
            {mode === "form" && (
              <button onClick={() => { setMode("search"); setSelectedItem(null); }} className="p-1 hover:bg-neutral-100">
                <CaretDown size={16} weight="bold" className="rotate-90" />
              </button>
            )}
            <h2 className="font-heading text-base font-black uppercase">Add Sale</h2>
          </div>
          <button onClick={onClose} className="p-1"><X size={18} weight="bold" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {mode === "search" && (
            <div className="p-4 space-y-3">
              <div>
                <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Search Your Items</label>
                <div className="relative">
                  <input type="text" value={query} onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search by brand, title, keywords..."
                    className="w-full p-2 pr-8 border-2 border-black font-body text-sm" autoFocus data-testid="sale-search-input" />
                  {searching && <Spinner size={14} className="animate-spin absolute right-2 top-3 text-neutral-400" />}
                </div>
              </div>
              {results.length > 0 && (
                <div className="border-2 border-black divide-y divide-neutral-200 max-h-60 overflow-y-auto">
                  {results.map((item) => (
                    <button key={item.id} onClick={() => selectItem(item)}
                      className="w-full p-2.5 flex items-center gap-2.5 text-left hover:bg-neutral-50"
                      data-testid={`search-result-${item.id}`}>
                      {item.thumbnail ? (
                        <img src={`data:image/jpeg;base64,${item.thumbnail}`} alt="" className="w-10 h-10 object-cover border border-black shrink-0" />
                      ) : item.photos?.[0] ? (
                        <img src={`/api/images/file/${item.photos[0]}`} alt="" className="w-10 h-10 object-cover border border-black shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-neutral-100 border border-black shrink-0 flex items-center justify-center">
                          <Package size={14} className="text-neutral-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-body text-xs font-semibold truncate">{item.title_keywords || item.brand}</div>
                        <div className="font-body text-[10px] text-neutral-500">
                          {item.brand && <span>{item.brand}</span>}
                          {item.list_price_high && <span className="ml-1">${item.list_price_low}-${item.list_price_high}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {query && !searching && results.length === 0 && (
                <p className="font-body text-xs text-neutral-400 text-center py-2">No matching items found</p>
              )}
              <button onClick={goManual}
                className="w-full p-3 border-2 border-dashed border-black font-heading text-xs font-bold uppercase flex items-center justify-center gap-2 hover:bg-neutral-50"
                data-testid="manual-entry-btn">
                <Plus size={14} weight="bold" /> Enter Manually
              </button>
            </div>
          )}

          {mode === "form" && (
            <div className="p-4 space-y-3">
              {selectedItem && (
                <div className="flex items-center gap-2.5 p-2 border-2 border-black bg-neutral-50">
                  {selectedItem.thumbnail ? (
                    <img src={`data:image/jpeg;base64,${selectedItem.thumbnail}`} alt="" className="w-12 h-12 object-cover border border-black shrink-0" />
                  ) : selectedItem.photos?.[0] ? (
                    <img src={`/api/images/file/${selectedItem.photos[0]}`} alt="" className="w-12 h-12 object-cover border border-black shrink-0" />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-xs font-bold truncate">{selectedItem.title_keywords}</div>
                    <div className="font-body text-[10px] text-neutral-500">{selectedItem.brand}</div>
                  </div>
                </div>
              )}
              <div>
                <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Item Name *</label>
                <input type="text" value={form.item_name} onChange={(e) => set("item_name", e.target.value)}
                  placeholder="e.g. Nike Dri-FIT Polo" className="w-full p-1.5 border-2 border-black font-body text-sm" data-testid="sale-item-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Brand</label>
                  <input type="text" value={form.brand} onChange={(e) => set("brand", e.target.value)}
                    placeholder="e.g. Nike" className="w-full p-1.5 border-2 border-black font-body text-sm" data-testid="sale-brand" />
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Platform</label>
                  <select value={form.platform} onChange={(e) => set("platform", e.target.value)}
                    className="w-full p-1.5 border-2 border-black font-body text-sm bg-white" data-testid="sale-platform">
                    <option value="eBay">eBay</option>
                    <option value="Poshmark">Poshmark</option>
                    <option value="Mercari">Mercari</option>
                    <option value="Facebook">Facebook Marketplace</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {moneyField("sale_price", "Sale Price *", "sale-price")}
                {moneyField("fees", "Platform Fees", "sale-fees")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {moneyField("shipping_cost", "Shipping Cost", "sale-shipping")}
                {moneyField("cogs", "Cost of Goods", "sale-cogs")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Sale Date</label>
                  <input type="date" value={form.sale_date} onChange={(e) => set("sale_date", e.target.value)}
                    className="w-full p-1.5 border-2 border-black font-body text-sm" data-testid="sale-date" />
                </div>
                <div>
                  <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Link to Haul</label>
                  <select value={form.haul_id} onChange={(e) => set("haul_id", e.target.value)}
                    className="w-full p-1.5 border-2 border-black font-body text-sm bg-white" data-testid="sale-haul">
                    <option value="">None</option>
                    {hauls.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-body text-[10px] font-semibold uppercase text-neutral-500">Notes</label>
                <input type="text" value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  placeholder="Optional notes" className="w-full p-1.5 border-2 border-black font-body text-sm" data-testid="sale-notes" />
              </div>
              {price > 0 && (
                <div className={`p-3 border-2 border-black text-center ${profit >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                  <div className="font-body text-[10px] uppercase text-neutral-500">Net Profit</div>
                  <div className={`font-heading text-xl font-black ${profit >= 0 ? "text-green-700" : "text-red-600"}`}>
                    {profit >= 0 ? "+" : ""}${profit.toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {mode === "form" && (
          <div className="p-4 border-t-2 border-black shrink-0">
            <button onClick={handleSave} disabled={saving || !form.item_name || !form.sale_price}
              className="w-full p-3 bg-black text-white font-heading font-bold uppercase text-sm border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none disabled:opacity-50"
              data-testid="save-sale-btn">
              {saving ? "Saving..." : "Save Sale"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(isoDate) {
  if (!isoDate) return "never";
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
